import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Loader } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Auth.css';



const Auth = () => {
  const navigate = useNavigate();
  const { login, wishlistCount, cartCount } = useCart();
  
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Form Validation
    if (!isLoginMode) {
      if (!formData.name.trim()) {
        setError('Full Name is required.');
        return;
      }
      if (!formData.phone.trim()) {
        setError('Phone Number is required.');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setLoading(true);
    const endpoint = isLoginMode ? '/user/auth/login' : '/user/auth/signup';
    const payload = isLoginMode 
      ? { email: formData.email, password: formData.password }
      : { name: formData.name, email: formData.email, phone: formData.phone, password: formData.password };

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      // Save token and user details in CartContext
      login(data.token, data.user);
      
      // Redirect to homepage or cart
      navigate('/');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <TopBar />
      <Header wishlistCount={wishlistCount} cartCount={cartCount} />
      
      <main className="auth-main-container container">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-logo-placeholder">
              <img src="/images/logo.jpg" alt="Logo" className="auth-logo-img" />
            </div>
            <h2 className="auth-card-title">
              {isLoginMode ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="auth-card-subtitle">
              {isLoginMode 
                ? 'Log in to view and save your shopping cart' 
                : 'Join us to store your cart items securely'}
            </p>
          </div>

          {error && <div className="auth-error-msg">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            {!isLoginMode && (
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input 
                  type="text" 
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  required
                />
              </div>
            )}

            {!isLoginMode && (
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input 
                  type="tel" 
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g. 9876543210"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input 
                type="email" 
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. john@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input 
                type="password" 
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                required
              />
            </div>

            {!isLoginMode && (
              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <input 
                  type="password" 
                  name="confirmPassword"
                  className="form-input"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  required
                />
              </div>
            )}

            <button type="submit" className="btn-auth-primary" disabled={loading}>
              {loading ? (
                <Loader size={18} className="spin-animation" />
              ) : isLoginMode ? (
                <>
                  <LogIn size={18} />
                  <span>Log In</span>
                </>
              ) : (
                <>
                  <UserPlus size={18} />
                  <span>Sign Up</span>
                </>
              )}
            </button>
          </form>

          <div className="auth-toggle-link">
            {isLoginMode ? "Don't have an account?" : 'Already have an account?'}
            <button 
              className="auth-toggle-btn"
              onClick={() => {
                setError('');
                setIsLoginMode(!isLoginMode);
              }}
            >
              {isLoginMode ? 'Sign Up' : 'Log In'}
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Auth;
